import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@shared/hooks';
import { useCustomFields } from '@features/products/hooks/useCustomFields';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { useAmazonConnector } from '@features/connections/amazon/hooks/useAmazon';
import { container } from '@infrastructure/container';
import { AmazonProductMatchMapping, DEFAULT_AMAZON_PRODUCT_MATCH_MAPPING } from '@domain/connection/models/ConnectionSettings';
import { AmazonMarketplaceOption } from '@domain/connection/models/AmazonMatchPreview';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import InventoryStep from '@features/connections/setup/InventoryStep';
import ImportProgress from '@features/connections/setup/ImportProgress';
import SetupFrame from '@features/connections/setup/SetupFrame';
import ProductsStep from './setup/ProductsStep';
import StoresStep from './setup/StoresStep';
import { SETUP_STEPS, WizardStep } from './setup/types';
import { LoadingIndicator } from '@shared/ui';

const AmazonConnectionSetup = () => {
  const { id: connectionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { connector } = useAmazonConnector();
  const { customFields: customFieldsResponse } = useCustomFields();
  const { warehouses, createWarehouse } = useWarehouses();

  const [step, setStep] = useState<WizardStep>('stores');
  const [productsImported, setProductsImported] = useState(false);
  const [importing, setImporting] = useState(false);
  const [productJobId, setProductJobId] = useState<string>();
  const [inventoryJobId, setInventoryJobId] = useState<string>();
  const [marketplaces, setMarketplaces] = useState<AmazonMarketplaceOption[]>([]);
  const [isLoadingMarketplaces, setIsLoadingMarketplaces] = useState(true);
  const [titleCountryCode, setTitleCountryCode] = useState('');
  const [mapping, setMapping] = useState<AmazonProductMatchMapping>(DEFAULT_AMAZON_PRODUCT_MATCH_MAPPING);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadInventory, setLoadInventory] = useState<boolean | null>(null);
  const [warehouseName, setWarehouseName] = useState('Amazon');
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyCustomFields = customFieldsResponse?.status === 'success' ? customFieldsResponse.data : [];
  const selectedMarketplaces = marketplaces.filter(marketplace => marketplace.selected);
  const countryOptions = useMemo(() => {
    const seen = new Set<string>();
    return selectedMarketplaces.flatMap(marketplace => {
      const code = marketplace.countryCode?.trim();
      if (!code || seen.has(code)) return [];
      seen.add(code);
      return [{ code, name: marketplace.name }];
    });
  }, [selectedMarketplaces]);

  const targetOptions = [
    { value: 'sku', label: 'SKU' },
    ...companyCustomFields.map(field => ({ value: `customFields.${field.key}`, label: field.label })),
  ];

  useEffect(() => {
    if (!connectionId || !isAuthenticated) return;
    let cancelled = false;
    setIsLoadingMarketplaces(true);
    container.amazonConnectorRepository.listMarketplaces(connectionId)
      .then(response => {
        if (cancelled) return;
        if (response.status === 'error') {
          setError(response.error.message ?? 'Could not load the stores');
          return;
        }
        setMarketplaces(response.data);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMarketplaces(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, isAuthenticated]);

  if (authLoading) return null;
  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;
  if (!connectionId) return <Navigate to="/company" replace />;

  const toggleMarketplace = (id: string) => {
    setMarketplaces(current => current.map(marketplace =>
      marketplace.id === id ? { ...marketplace, selected: !marketplace.selected } : marketplace,
    ));
  };

  const saveStores = async () => {
    if (selectedMarketplaces.length === 0) {
      setError('Select at least one store');
      return;
    }
    setBusy(true);
    setError(null);
    const response = await container.amazonConnectorRepository.saveMarketplaces(
      connectionId,
      selectedMarketplaces.map(marketplace => marketplace.id),
    );
    setBusy(false);
    if (response.status === 'error') {
      setError(response.error.message ?? 'Could not save the stores');
      return;
    }
    setTitleCountryCode(current => current || countryOptions[0]?.code || '');
    setStep('products');
  };

  const continueProducts = () => {
    setProductsImported(true);
    setStep('inventory');
  };

  const finish = async () => {
    if (loadInventory == null) return;
    setBusy(true);
    setError(null);
    try {
      if (loadInventory) {
        await createWarehouse({
          name: warehouseName.trim() || 'Amazon',
          location: warehouseLocation.trim() || undefined,
          warehouseConnections: [{
            sourceType: WarehouseConnectionType.AMAZON,
            mutationType: MutationType.SYNC,
            rules: [{
              operator: RuleOperator.EQUALS,
              field: RuleField.CONNECTION_ID,
              value: connectionId,
            }],
          }],
        });
      }
      const setup = await container.amazonConnectorRepository.completeSetup(connectionId, {
        productMatchMapping: mapping,
        titleCountryCode: titleCountryCode || undefined,
      });
      if (setup.status === 'error') throw new Error(setup.error.message ?? 'Could not import the products');
      const productJob = setup.data.jobs.find(job => job.target === 'products')?.jobId ?? setup.data.jobs[0]?.jobId;
      if (!productJob) throw new Error('The product import did not start');
      const response = await container.amazonConnectorRepository.finishSetup(connectionId, { loadInventory });
      if (response.status === 'error') throw new Error(response.error.message);
      const inventoryJob = loadInventory ? response.data.jobs.find(job => job.target === 'inventory')?.jobId : undefined;
      if (loadInventory && !inventoryJob) throw new Error('Could not start the inventory import');
      setProductJobId(productJob);
      setInventoryJobId(inventoryJob);
      setBusy(false);
      setImporting(true);
    } catch (finishError) {
      setError(finishError instanceof Error ? finishError.message : 'Could not load inventory');
      setBusy(false);
    }
  };

  const goToStep = (next: WizardStep) => {
    if (busy || importing) return;
    if (next === 'stores') setStep('stores');
    if (next === 'products' && (step === 'inventory' || productsImported || step === 'products')) setStep('products');
    if (next === 'inventory' && productsImported) setStep('inventory');
  };

  const primaryLabel = step === 'inventory' ? 'Finish' : 'Next';
  const primaryDisabled = step === 'inventory'
    ? loadInventory == null || (loadInventory && !warehouseName.trim())
    : step === 'products' && previewLoading;

  if (!connector) return <LoadingIndicator />;

  if (importing) {
    return (
      <ImportProgress
        connector={connector}
        connectionId={connectionId}
        title="Importing your Amazon data"
        description={inventoryJobId ? 'You can continue when products and inventory are finished.' : 'You can continue when the product import is finished.'}
        footer="You can continue when products and inventory are finished."
        onContinue={() => navigate('/product-catalog/products')}
      />
    );
  }

  return (
    <SetupFrame
      connector={connector}
      steps={SETUP_STEPS}
      step={step}
      busy={busy}
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled}
      onPrimary={step === 'stores' ? saveStores : step === 'products' ? continueProducts : finish}
      onClose={() => navigate('/company')}
      onStepChange={next => goToStep(next as WizardStep)}
      error={error}
    >

      {step === 'stores' && (
        <StoresStep marketplaces={marketplaces} loading={isLoadingMarketplaces} onToggle={toggleMarketplace} />
      )}
      {step === 'products' && (
        <ProductsStep
          connectionId={connectionId}
          countryOptions={countryOptions}
          titleCountryCode={titleCountryCode}
          onTitleCountryChange={setTitleCountryCode}
          mapping={mapping}
          onMappingChange={setMapping}
          targetOptions={targetOptions}
          onLoadingChange={setPreviewLoading}
        />
      )}
      {step === 'inventory' && (
        <InventoryStep
          description="Choose whether to import inventory from Amazon."
          choiceCaption="Amazon inventory"
          loadInventory={loadInventory}
          onLoadInventoryChange={setLoadInventory}
          warehouses={(warehouses ?? []).map(warehouse => ({ id: warehouse.id, name: warehouse.name }))}
          warehouseName={warehouseName}
          warehouseLocation={warehouseLocation}
          onWarehouseNameChange={setWarehouseName}
          onWarehouseLocationChange={setWarehouseLocation}
        />
      )}
    </SetupFrame>

  );
};

export default AmazonConnectionSetup;
